import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Search,
  Eye,
  ShoppingCart,
  Calendar,
  Trash2,
  AlertTriangle,
  Loader2,
  MoreHorizontal,
  Download,
  CheckCircle2,
  Clock,
  IndianRupee,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { TablePagination } from '@/components/TablePagination';
import { format } from 'date-fns';

import { API_URL } from '@/lib/api';
import { downloadOrderReceipt } from '@/lib/receipt';

const statusColors = {
  pending:   'status-pending',
  confirmed: 'status-confirmed',
  packed:    'bg-blue-100 text-blue-700 border border-blue-200',
  shipped:   'bg-indigo-100 text-indigo-700 border border-indigo-200',
  delivered: 'status-delivered',
  cancelled: 'status-cancelled',
};

const Orders = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    store_id: 'all',
    status: 'all',
    payment_method: 'all',
    start_date: null,
    end_date: null,
  });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [receiptLoadingId, setReceiptLoadingId] = useState(null);

  useEffect(() => {
    fetchStores();
    fetchOrders();
  }, [filters]);

  useEffect(() => {
    setPage(1);
  }, [filters]);

  const mapOrder = (o) => ({
    id: o.id,
    order_id: o.orderNumber,
    store_name: o.storeName,
    store_id: o.storeId,
    created_at: o.createdAt,
    grand_total: o.totalAmount,
    payment_method: (o.paymentMethod || '').toLowerCase(),
    payment_status: (o.paymentStatus || '').toLowerCase(),
    order_status: (o.orderStatus || '').toLowerCase(),
  });

  const fetchStores = async () => {
    try {
      const res = await axios.get(`${API_URL}/stores`);
      setStores((res.data || []).map(s => ({ id: s.id, store_name: s.storeName })));
    } catch (error) {
      console.error('Failed to load stores:', error);
    }
  };

  const fetchOrders = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.store_id && filters.store_id !== 'all') params.append('storeId', filters.store_id);
      if (filters.status && filters.status !== 'all') params.append('status', filters.status.toUpperCase());
      if (filters.payment_method && filters.payment_method !== 'all') params.append('paymentMethod', filters.payment_method.toUpperCase());
      if (filters.start_date) params.append('startDate', filters.start_date.toISOString());
      if (filters.end_date) params.append('endDate', filters.end_date.toISOString());

      const response = await axios.get(`${API_URL}/orders?${params}`);
      setOrders((response.data || []).map(mapOrder));
    } catch (error) {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReceipt = async (orderRow, e) => {
    e?.preventDefault?.();
    setReceiptLoadingId(orderRow.id);
    try {
      await downloadOrderReceipt(orderRow.id, orderRow.order_id);
      toast.success('Receipt downloaded');
    } catch (err) {
      toast.error(err?.message || 'Failed to download receipt');
    } finally {
      setReceiptLoadingId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await axios.delete(`${API_URL}/orders/${deleteTarget.id}`);
      toast.success('Order deleted');
      setDeleteTarget(null);
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div data-testid="orders-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Orders</h1>
          <p className="text-slate-500 mt-1">Manage and track all orders</p>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <Select value={filters.store_id} onValueChange={(value) => setFilters(prev => ({ ...prev, store_id: value }))}>
          <SelectTrigger data-testid="store-filter">
            <SelectValue placeholder="All Stores" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Stores</SelectItem>
            {stores.map((store) => (
              <SelectItem key={store.id} value={store.id}>{store.store_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={filters.status} onValueChange={(value) => setFilters(prev => ({ ...prev, status: value }))}>
          <SelectTrigger data-testid="status-filter">
            <SelectValue placeholder="Order Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="confirmed">Confirmed</SelectItem>
            <SelectItem value="packed">Packed</SelectItem>
            <SelectItem value="shipped">Shipped</SelectItem>
            <SelectItem value="delivered">Delivered</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filters.payment_method} onValueChange={(value) => setFilters(prev => ({ ...prev, payment_method: value }))}>
          <SelectTrigger data-testid="payment-filter">
            <SelectValue placeholder="Payment Method" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Methods</SelectItem>
            <SelectItem value="cod">COD</SelectItem>
            <SelectItem value="online">Online</SelectItem>
          </SelectContent>
        </Select>

        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="justify-start text-left font-normal" data-testid="start-date-picker">
              <Calendar className="mr-2 h-4 w-4" />
              {filters.start_date ? format(filters.start_date, 'PP') : 'Start Date'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <CalendarComponent
              mode="single"
              selected={filters.start_date}
              onSelect={(date) => setFilters(prev => ({ ...prev, start_date: date }))}
              initialFocus
            />
          </PopoverContent>
        </Popover>

        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="justify-start text-left font-normal" data-testid="end-date-picker">
              <Calendar className="mr-2 h-4 w-4" />
              {filters.end_date ? format(filters.end_date, 'PP') : 'End Date'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <CalendarComponent
              mode="single"
              selected={filters.end_date}
              onSelect={(date) => setFilters(prev => ({ ...prev, end_date: date }))}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-card">
        {loading ? (
          <div className="p-4 space-y-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="flex items-center gap-4 animate-fade-in" style={{ animationDelay: `${i * 50}ms` }}>
                <div className="h-3.5 rounded-lg shimmer w-28" />
                <div className="h-3.5 rounded-lg shimmer w-36 flex-1" />
                <div className="h-3.5 rounded-lg shimmer w-20" />
                <div className="h-3.5 rounded-lg shimmer w-20" />
                <div className="h-6 rounded-full shimmer w-14" />
                <div className="h-6 rounded-full shimmer w-20" />
                <div className="h-8 rounded-xl shimmer w-16" />
              </div>
            ))}
          </div>
        ) : orders.length === 0 ? (
          <div className="empty-state">
            <div className="w-16 h-16 rounded-2xl bg-purple-50 flex items-center justify-center mb-4">
              <ShoppingCart className="w-8 h-8 text-purple-400" />
            </div>
            <p className="text-base font-semibold text-slate-700">No orders found</p>
            <p className="text-sm text-slate-400 mt-1">Orders will appear here when stores place them</p>
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Order #</TableHead>
                  <TableHead>Store</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead>Order Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.slice((page - 1) * pageSize, page * pageSize).map((order) => (
                  <TableRow
                    key={order.id}
                    className="table-row-hover"
                    data-testid={`order-row-${order.id}`}
                  >
                    <TableCell className="font-mono text-sm">{order.order_id}</TableCell>
                    <TableCell className="font-medium">{order.store_name}</TableCell>
                    <TableCell className="text-slate-500">
                      {new Date(order.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="font-semibold">
                      ₹{order.grand_total?.toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="uppercase text-xs">
                        {order.payment_method}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={statusColors[order.order_status] || 'status-pending'}>
                        {order.order_status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid={`order-actions-${order.id}`}>
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/orders/${order.id}`)}>
                              <Eye className="w-4 h-4 mr-2" /> View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) => handleDownloadReceipt(order, e)}
                              disabled={receiptLoadingId === order.id}
                            >
                              {receiptLoadingId === order.id ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <Download className="w-4 h-4 mr-2" />
                              )}
                              Download Receipt
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => setDeleteTarget(order)}
                              className="text-rose-600 focus:text-rose-600 focus:bg-rose-50"
                            >
                              <Trash2 className="w-4 h-4 mr-2" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
            </Table>
            <TablePagination
              data={orders}
              page={page}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={(v) => { setPageSize(v); setPage(1); }}
            />
          </>
        )}
      </div>

      {/* Delete confirm dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-600">
              <AlertTriangle className="w-5 h-5" /> Delete Order
            </DialogTitle>
          </DialogHeader>
          <div className="py-3">
            <p className="text-sm text-slate-700">
              Are you sure you want to delete order <strong>{deleteTarget?.order_id}</strong>?
            </p>
            <p className="text-xs text-slate-500 mt-1">This will permanently remove the order and all its items. This cannot be undone.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button onClick={handleDelete} disabled={isDeleting} className="bg-rose-600 hover:bg-rose-700 text-white">
              {isDeleting && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
              <Trash2 className="w-4 h-4 mr-1" /> Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Orders;
